import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronDown, X, Search, MapPin } from 'lucide-react';
import produtosUsadosOSService from 'features/estoque/services/produtosUsadosOSService';
import OSSEstoqueDashboard from './Components/OSSEstoqueDashboard';
import { DateRangeFilterBlur } from 'components/filters';
import ClearCacheButton from 'features/estoque/components/ClearCacheButton';
import PeriodoDisplay from '../PeriodoDisplay';

const OSSEstoque = () => {
  const [dadosCompilados, setDadosCompilados] = useState({});
  const [loading, setLoading] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [assuntosSelecionados, setAssuntosSelecionados] = useState([]);
  const [assuntosSelecionadosDraft, setAssuntosSelecionadosDraft] = useState([]);
  const [dropdownAssuntosAberto, setDropdownAssuntosAberto] = useState(false);
  const [buscaAssunto, setBuscaAssunto] = useState('');
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState([]);
  const [cidadesSelecionadasDraft, setCidadesSelecionadasDraft] = useState([]);
  const [dropdownCidadesAberto, setDropdownCidadesAberto] = useState(false);
  const [buscaCidade, setBuscaCidade] = useState('');
  const [retryInfo, setRetryInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const dropdownAssuntosRef = useRef(null);
  const dropdownCidadesRef = useRef(null);
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setRetryInfo(null);
      setLoadError(null);

      const response = await produtosUsadosOSService.buscarOSSEstoque({
        data_inicio: range.start,
        data_fim: range.end,
        assuntos: assuntosSelecionados.length > 0 ? assuntosSelecionados : undefined,
        cidades: cidadesSelecionadas.length > 0 ? cidadesSelecionadas : undefined,
      });

      if (response?.success === true && response?.data && typeof response.data === 'object') {
        setDadosCompilados(response.data);
      } else {
        throw new Error(response?.error || 'Resposta inválida ao carregar OSs Estoque');
      }
      setUltimaAtualizacao(new Date());
      setRetryInfo(null); // Limpar info de retry em caso de sucesso
    } catch (error) {
      console.error('❌ Erro ao carregar dados OSS Estoque:', error);
      setRetryInfo(null); // Limpar info de retry em caso de erro final
      setLoadError(error?.response?.data?.message || error?.message || 'Erro ao carregar OSs Estoque');
    } finally {
      setLoading(false);
    }
  }, [assuntosSelecionados, cidadesSelecionadas, range.end, range.start]);

  // Carregar dados ao montar o componente
  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    const handleCompanyChange = () => {
      carregarDados();
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarDados]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const selecoesIguais = (a = [], b = []) => (
      a.length === b.length && a.every((item) => b.includes(item))
    );

    const aplicarAssuntosSeMudou = () => {
      if (!selecoesIguais(assuntosSelecionados, assuntosSelecionadosDraft)) {
        setAssuntosSelecionados(assuntosSelecionadosDraft);
      }
    };

    const aplicarCidadesSeMudou = () => {
      if (!selecoesIguais(cidadesSelecionadas, cidadesSelecionadasDraft)) {
        setCidadesSelecionadas(cidadesSelecionadasDraft);
      }
    };

    const handler = (e) => {
      if (dropdownAssuntosRef.current && !dropdownAssuntosRef.current.contains(e.target)) {
        if (dropdownAssuntosAberto) {
          aplicarAssuntosSeMudou();
        }
        setDropdownAssuntosAberto(false);
      }
      if (dropdownCidadesRef.current && !dropdownCidadesRef.current.contains(e.target)) {
        if (dropdownCidadesAberto) {
          aplicarCidadesSeMudou();
        }
        setDropdownCidadesAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [
    assuntosSelecionados,
    assuntosSelecionadosDraft,
    cidadesSelecionadas,
    cidadesSelecionadasDraft,
    dropdownAssuntosAberto,
    dropdownCidadesAberto
  ]);

  // Filtrar assuntos baseado na busca
  const assuntosFiltrados = useMemo(() => {
    const assuntosDisponiveis = dadosCompilados.assuntos_disponiveis || [];
    
    if (!buscaAssunto.trim()) {
      return assuntosDisponiveis;
    }
    
    const termoBusca = buscaAssunto.toLowerCase().trim();
    return assuntosDisponiveis.filter(assunto =>
      assunto.toLowerCase().includes(termoBusca)
    );
  }, [dadosCompilados.assuntos_disponiveis, buscaAssunto]);

  const cidadesDisponiveis = useMemo(() => {
    const cidades = new Set();
    const fontes = [
      ...(dadosCompilados?.por_cidade?.oss || []),
      ...(dadosCompilados?.detalhamento?.oss || []),
      ...(dadosCompilados?.por_bairro?.oss || [])
    ];

    fontes.forEach((item) => {
      const cidade = item?.CIDADE || item?.cidade;
      if (cidade) cidades.add(cidade);
    });

    return Array.from(cidades).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [dadosCompilados?.por_cidade?.oss, dadosCompilados?.detalhamento?.oss, dadosCompilados?.por_bairro?.oss]);

  const cidadesFiltradas = useMemo(() => {
    if (!buscaCidade.trim()) return cidadesDisponiveis;
    const termoBusca = buscaCidade.toLowerCase().trim();
    return cidadesDisponiveis.filter((cidade) => cidade.toLowerCase().includes(termoBusca));
  }, [cidadesDisponiveis, buscaCidade]);

  // Dados filtrados por assunto
  const dadosFiltrados = useMemo(() => {
    const normalizarValor = (valor) => String(valor || '').trim().toLowerCase();
    const assuntosSelecionadosNormalizados = new Set(
      (assuntosSelecionados || []).map(normalizarValor).filter(Boolean)
    );
    const cidadesSelecionadasNormalizadas = new Set(
      (cidadesSelecionadas || []).map(normalizarValor).filter(Boolean)
    );

    const filtrarPorAssuntoECidade = (dados = []) => {
      let resultado = dados;

      if (assuntosSelecionados && assuntosSelecionados.length > 0) {
        resultado = resultado.filter((item) => {
          const assuntoItem = normalizarValor(item.ASSUNTO || item.assunto || item.Assunto || '');
          return assuntosSelecionadosNormalizados.has(assuntoItem);
        });
      }

      if (cidadesSelecionadas && cidadesSelecionadas.length > 0) {
        const temCampoCidade = resultado.some((item) => (item.CIDADE || item.cidade || item.Cidade));
        if (temCampoCidade) {
          resultado = resultado.filter((item) => {
            const cidadeItem = normalizarValor(item.CIDADE || item.cidade || item.Cidade || '');
            return cidadesSelecionadasNormalizadas.has(cidadeItem);
          });
        }
      }

      return resultado;
    };

    // Função para filtrar dados por assunto/cidade
    const filtrarPorAssuntoECidadeItem = (item) => {
      if (!item) return false;
      if (assuntosSelecionados && assuntosSelecionados.length > 0) {
        const assuntoItem = normalizarValor(item.ASSUNTO || item.assunto || item.Assunto || '');
        if (!assuntosSelecionadosNormalizados.has(assuntoItem)) {
          return false;
        }
      }
      if (cidadesSelecionadas && cidadesSelecionadas.length > 0) {
        const cidadeItem = normalizarValor(item.CIDADE || item.cidade || item.Cidade || '');
        if (cidadeItem && !cidadesSelecionadasNormalizadas.has(cidadeItem)) {
          return false;
        }
      }
      return true;
    };

    const calcularMediaPorPeriodo = (total) => {
      const dataInicio = new Date(range.start);
      const dataFim = new Date(range.end);
      const diffTime = Math.abs(dataFim - dataInicio);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      if (diffDays <= 0) return 0;
      return total / diffDays;
    };

    const parseDataHora = (valor) => {
      if (!valor) return null;
      const texto = String(valor).trim();
      if (!texto) return null;

      if (texto.includes('/')) {
        const [dataParte, horaParte = '00:00:00'] = texto.split(' ');
        const [dia, mes, ano] = dataParte.split('/');
        if (!dia || !mes || !ano) return null;
        const iso = `${ano.padStart(4, '0')}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}T${horaParte}`;
        const date = new Date(iso);
        return Number.isNaN(date.getTime()) ? null : date;
      }

      const date = new Date(texto.replace(' ', 'T'));
      return Number.isNaN(date.getTime()) ? null : date;
    };

    // Processar KPIs com filtros de assunto/cidade
    const processarKPIs = () => {
      const kpis = dadosCompilados.kpis || {};
      const temFiltroAssunto = assuntosSelecionados && assuntosSelecionados.length > 0;
      const temFiltroCidade = cidadesSelecionadas && cidadesSelecionadas.length > 0;
      const temFiltros = temFiltroAssunto || temFiltroCidade;

      // Sem filtros aplicados: usar TOTAL e recalcular média no período para manter consistência
      if (!temFiltros) {
        const totalBackend = parseInt(kpis?.agregado?.TOTAL || 0, 10);
        return {
          TOTAL: totalBackend,
          media_por_dia: calcularMediaPorPeriodo(totalBackend)
        };
      }

      // Prioridade 1: calcular total por ID único de OS no detalhamento filtrado
      const detalhamentoFiltrado = filtrarPorAssuntoECidade(dadosCompilados.detalhamento?.oss || []);
      if (detalhamentoFiltrado.length > 0) {
        const osUnicas = new Set();
        detalhamentoFiltrado.forEach((item) => {
          const osId = item['ID OS'] ?? item.id ?? item.ID ?? '';
          if (osId !== '') {
            osUnicas.add(String(osId));
          }
        });
        const total = osUnicas.size;
        return {
          TOTAL: total,
          media_por_dia: calcularMediaPorPeriodo(total)
        };
      }

      // Prioridade 2: fallback por dia (somatório de TOTAL)
      const porDiaFiltrado = filtrarPorAssuntoECidade(dadosCompilados.por_dia?.oss || []);
      if (porDiaFiltrado.length > 0) {
        const total = porDiaFiltrado.reduce((sum, item) => sum + parseInt(item.TOTAL || item.total || 0, 10), 0);
        return {
          TOTAL: total,
          media_por_dia: calcularMediaPorPeriodo(total)
        };
      }

      // Prioridade 3: fallback legado por assunto (mantém compatibilidade)
      if (kpis.por_assunto && Array.isArray(kpis.por_assunto)) {
        const kpisFiltrados = kpis.por_assunto.filter((kpi) => filtrarPorAssuntoECidadeItem(kpi));
        const total = kpisFiltrados.reduce((sum, kpi) => sum + parseInt(kpi.TOTAL || 0, 10), 0);
        return {
          TOTAL: total,
          media_por_dia: calcularMediaPorPeriodo(total)
        };
      }

      return { TOTAL: 0, media_por_dia: 0 };
    };

    const processarPorDia = () => {
      const porDia = dadosCompilados.por_dia || {};
      const detalhamento = dadosCompilados.detalhamento?.oss || [];
      const temFiltroAssunto = assuntosSelecionados && assuntosSelecionados.length > 0;
      const temFiltroCidade = cidadesSelecionadas && cidadesSelecionadas.length > 0;

      if (!porDia.oss) return porDia;
      if (!temFiltroAssunto && !temFiltroCidade) return porDia;

      const detalhamentoFiltrado = filtrarPorAssuntoECidade(detalhamento);
      const agrupados = {};
      let conseguiuRecalcular = false;

      detalhamentoFiltrado.forEach((item) => {
        const fechamento = parseDataHora(item.FECHAMENTO || item.fechamento || item.ABERTURA || item.abertura);
        if (!fechamento) return;
        const dia = String(fechamento.getDate()).padStart(2, '0');
        agrupados[dia] = (agrupados[dia] || 0) + 1;
        conseguiuRecalcular = true;
      });

      if (conseguiuRecalcular) {
        return {
          oss: Object.entries(agrupados)
            .map(([dia, total]) => ({ fechamento: dia, TOTAL: total }))
            .sort((a, b) => parseInt(a.fechamento, 10) - parseInt(b.fechamento, 10))
        };
      }

      return { oss: porDia.oss.filter((item) => filtrarPorAssuntoECidadeItem(item)) };
    };

    const processarPorMes = () => {
      const porMes = dadosCompilados.por_mes || {};
      const detalhamento = dadosCompilados.detalhamento?.oss || [];
      const temFiltroAssunto = assuntosSelecionados && assuntosSelecionados.length > 0;
      const temFiltroCidade = cidadesSelecionadas && cidadesSelecionadas.length > 0;

      if (!porMes.oss) return porMes;
      if (!temFiltroAssunto && !temFiltroCidade) return porMes;

      const detalhamentoFiltrado = filtrarPorAssuntoECidade(detalhamento);
      const agrupados = {};
      let conseguiuRecalcular = false;

      detalhamentoFiltrado.forEach((item) => {
        const fechamento = parseDataHora(item.FECHAMENTO || item.fechamento || item.ABERTURA || item.abertura);
        if (!fechamento) return;
        const mes = String(fechamento.getMonth() + 1).padStart(2, '0');
        const ano = String(fechamento.getFullYear());
        const chave = `${mes}/${ano}`;
        agrupados[chave] = (agrupados[chave] || 0) + 1;
        conseguiuRecalcular = true;
      });

      if (conseguiuRecalcular) {
        return {
          oss: Object.entries(agrupados)
            .map(([mes, total]) => ({ MES: mes, TOTAL: total }))
            .sort((a, b) => {
              const [mesA, anoA] = String(a.MES || '').split('/');
              const [mesB, anoB] = String(b.MES || '').split('/');
              const da = new Date(parseInt(anoA || '0', 10), parseInt(mesA || '1', 10) - 1, 1);
              const db = new Date(parseInt(anoB || '0', 10), parseInt(mesB || '1', 10) - 1, 1);
              return da - db;
            })
        };
      }

      return { oss: porMes.oss.filter((item) => filtrarPorAssuntoECidadeItem(item)) };
    };

    // Processar por_tempo: se for array, filtrar e agregar quando há assunto/cidade selecionados
    const processarPorTempo = () => {
      const porTempo = dadosCompilados.por_tempo || {};
      const detalhamento = dadosCompilados.detalhamento?.oss || [];
      
      // Se não tem estrutura oss, retornar como está (objeto único)
      if (!porTempo.oss) {
        // Se for objeto único, converter para formato esperado
        if (porTempo['ATÉ 4H'] !== undefined) {
          return { oss: [porTempo] };
        }
        return porTempo;
      }
      
      const temFiltroAssunto = assuntosSelecionados && assuntosSelecionados.length > 0;
      const temFiltroCidade = cidadesSelecionadas && cidadesSelecionadas.length > 0;
      if (!temFiltroAssunto && !temFiltroCidade) {
        return porTempo;
      }

      const temposAgregados = {
        'ATÉ 4H': 0,
        'DE 4H À 8H': 0,
        'DE 8H À 12H': 0,
        'DE 12H À 24H': 0,
        'DE 24H À 48H': 0,
        'DE 48H À 72H': 0,
        'MAIOR QUE 72H': 0
      };

      const detalhamentoFiltrado = filtrarPorAssuntoECidade(detalhamento);
      let conseguiuRecalcular = false;

      detalhamentoFiltrado.forEach((item) => {
        const abertura = parseDataHora(item.ABERTURA || item.abertura);
        const fechamento = parseDataHora(item.FECHAMENTO || item.fechamento);
        if (!abertura || !fechamento) return;

        const diffHoras = (fechamento.getTime() - abertura.getTime()) / (1000 * 60 * 60);
        if (!Number.isFinite(diffHoras) || diffHoras < 0) return;
        conseguiuRecalcular = true;

        if (diffHoras <= 4) temposAgregados['ATÉ 4H'] += 1;
        else if (diffHoras <= 8) temposAgregados['DE 4H À 8H'] += 1;
        else if (diffHoras <= 12) temposAgregados['DE 8H À 12H'] += 1;
        else if (diffHoras <= 24) temposAgregados['DE 12H À 24H'] += 1;
        else if (diffHoras <= 48) temposAgregados['DE 24H À 48H'] += 1;
        else if (diffHoras <= 72) temposAgregados['DE 48H À 72H'] += 1;
        else temposAgregados['MAIOR QUE 72H'] += 1;
      });

      if (!conseguiuRecalcular) {
        const temposFiltrados = porTempo.oss.filter((item) => filtrarPorAssuntoECidadeItem(item));
        temposFiltrados.forEach((item) => {
          temposAgregados['ATÉ 4H'] += parseInt(item['ATÉ 4H'] || 0, 10);
          temposAgregados['DE 4H À 8H'] += parseInt(item['DE 4H À 8H'] || 0, 10);
          temposAgregados['DE 8H À 12H'] += parseInt(item['DE 8H À 12H'] || 0, 10);
          temposAgregados['DE 12H À 24H'] += parseInt(item['DE 12H À 24H'] || 0, 10);
          temposAgregados['DE 24H À 48H'] += parseInt(item['DE 24H À 48H'] || 0, 10);
          temposAgregados['DE 48H À 72H'] += parseInt(item['DE 48H À 72H'] || 0, 10);
          temposAgregados['MAIOR QUE 72H'] += parseInt(item['MAIOR QUE 72H'] || 0, 10);
        });
      }

      return {
        oss: [temposAgregados]
      };
    };

    return {
      kpis: processarKPIs(),
      por_dia: processarPorDia(),
      por_diagnostico: { oss: filtrarPorAssuntoECidade(dadosCompilados.por_diagnostico?.oss || []) },
      por_assunto: { oss: filtrarPorAssuntoECidade(dadosCompilados.por_assunto?.oss || []) },
      por_tempo: processarPorTempo(),
      por_tecnico: { oss: filtrarPorAssuntoECidade(dadosCompilados.por_tecnico?.oss || []) },
      por_cidade: { oss: filtrarPorAssuntoECidade(dadosCompilados.por_cidade?.oss || []) },
      por_bairro: { oss: filtrarPorAssuntoECidade(dadosCompilados.por_bairro?.oss || []) },
      por_mes: processarPorMes(),
      detalhamento: { oss: filtrarPorAssuntoECidade(dadosCompilados.detalhamento?.oss || []) },
      nao_finalizada: { oss: filtrarPorAssuntoECidade(dadosCompilados.nao_finalizada?.oss || []) },
      assuntos_disponiveis: dadosCompilados.assuntos_disponiveis || []
    };
  }, [dadosCompilados, assuntosSelecionados, cidadesSelecionadas, range]);

  // Toggle seleção de assunto
  const toggleAssunto = (assunto) => {
    setAssuntosSelecionadosDraft(prev => {
      if (prev.includes(assunto)) {
        return prev.filter(a => a !== assunto);
      } else {
        return [...prev, assunto];
      }
    });
  };

  // Limpar todos os assuntos selecionados
  const limparAssuntos = () => {
    setAssuntosSelecionadosDraft([]);
    setBuscaAssunto('');
  };

  const toggleCidade = (cidade) => {
    setCidadesSelecionadasDraft((prev) => {
      if (prev.includes(cidade)) {
        return prev.filter((c) => c !== cidade);
      }
      return [...prev, cidade];
    });
  };

  const limparCidades = () => {
    setCidadesSelecionadasDraft([]);
    setBuscaCidade('');
  };

  // Removido LoadingSpinner para permitir blur do DateRangeFilterBlur

  return (
    <div className="space-y-6">
      {/* Feedback de retry (opcional) */}
      {retryInfo && retryInfo.status === 'retrying' && (
        <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-md p-3">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-[color:var(--status-warn)] mr-2 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <p className="text-sm text-[color:var(--text-primary)]">
              Tentando novamente... (Tentativa {retryInfo.attempt}/{retryInfo.maxRetries})
            </p>
          </div>
        </div>
      )}
      {loadError && (
        <div className="bg-[color:var(--surface-muted)] border border-[color:var(--status-danger)] rounded-md p-3">
          <p className="text-sm text-[color:var(--status-danger)]">{loadError}</p>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-[color:var(--text-primary)]">Período</h3>
          <PeriodoDisplay start={range.start} end={range.end} />
          {loading ? (
            <div className="flex items-center text-sm text-[color:var(--accent)]">
              <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              Atualizando...
            </div>
          ) : (
            ultimaAtualizacao && (
              <div className="text-xs text-[color:var(--text-muted)]">Atualizado: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}</div>
            )
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Botão Recarregar Dados */}
          <ClearCacheButton
            endpoint="oss-estoque"
            params={{
              data_inicio: range.start,
              data_fim: range.end
            }}
            onReload={carregarDados}
          />
          {/* Filtro de Assunto */}
          <div ref={dropdownAssuntosRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownAssuntosAberto) {
                  const selecoesIguais = assuntosSelecionados.length === assuntosSelecionadosDraft.length
                    && assuntosSelecionados.every((item) => assuntosSelecionadosDraft.includes(item));
                  if (!selecoesIguais) {
                    setAssuntosSelecionados(assuntosSelecionadosDraft);
                  }
                  setDropdownAssuntosAberto(false);
                  return;
                }
                setAssuntosSelecionadosDraft(assuntosSelecionados);
                setDropdownAssuntosAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 0 
                  ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-white' 
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
              }`}
            >
              <span className="truncate">
                {(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length === 0 
                  ? 'Filtrar por Assunto' 
                  : `${(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length} assunto${(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 1 ? 's' : ''} selecionado${(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 1 ? 's' : ''}`
                }
              </span>
              {(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 0 && (
                <span className="inline-flex items-center rounded-full bg-[color:var(--accent)] px-2 py-0.5 text-xs font-medium text-white">
                  {(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownAssuntosAberto && (
              <div className="absolute z-50 mt-2 w-64 rounded-md bg-[color:var(--surface)] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none right-0">
                <div className="p-2 border-b border-[color:var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Filtrar por Assunto</span>
                    {assuntosSelecionadosDraft.length > 0 && (
                      <button
                        onClick={limparAssuntos}
                        className="text-xs text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  {/* Campo de busca */}
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaAssunto}
                      onChange={(e) => setBuscaAssunto(e.target.value)}
                      placeholder="Buscar assunto..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaAssunto && (
                      <button
                        onClick={() => setBuscaAssunto('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-1">
                  {assuntosFiltrados && assuntosFiltrados.length > 0 ? (
                    assuntosFiltrados.map((assunto) => {
                      const isSelected = assuntosSelecionadosDraft.includes(assunto);
                      return (
                        <label
                          key={assunto}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-md ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAssunto(assunto)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {assunto}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaAssunto ? 'Nenhum assunto encontrado' : 'Nenhum assunto disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtro de Cidade */}
          <div ref={dropdownCidadesRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownCidadesAberto) {
                  const selecoesIguais = cidadesSelecionadas.length === cidadesSelecionadasDraft.length
                    && cidadesSelecionadas.every((item) => cidadesSelecionadasDraft.includes(item));
                  if (!selecoesIguais) {
                    setCidadesSelecionadas(cidadesSelecionadasDraft);
                  }
                  setDropdownCidadesAberto(false);
                  return;
                }
                setCidadesSelecionadasDraft(cidadesSelecionadas);
                setDropdownCidadesAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 0
                  ? 'border-[color:var(--accent)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span className="truncate">
                {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length === 0
                  ? 'Filtrar por Cidade'
                  : `${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length} cidade${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 1 ? 's' : ''} selecionada${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 1 ? 's' : ''}`
                }
              </span>
              {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 0 && (
                <span className="inline-flex items-center rounded-full border border-[color:var(--accent)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-xs font-medium text-[color:var(--accent)]">
                  {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownCidadesAberto && (
              <div className="absolute z-50 mt-2 w-64 rounded-md bg-[color:var(--surface)] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none right-0">
                <div className="p-2 border-b border-[color:var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Filtrar por Cidade</span>
                    {cidadesSelecionadasDraft.length > 0 && (
                      <button
                        onClick={limparCidades}
                        className="text-xs text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaCidade}
                      onChange={(e) => setBuscaCidade(e.target.value)}
                      placeholder="Buscar cidade..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaCidade && (
                      <button
                        onClick={() => setBuscaCidade('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-1">
                  {cidadesFiltradas.length > 0 ? (
                    cidadesFiltradas.map((cidade) => {
                      const isSelected = cidadesSelecionadasDraft.includes(cidade);
                      return (
                        <label
                          key={cidade}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-md ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCidade(cidade)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {cidade}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaCidade ? 'Nenhuma cidade encontrada' : 'Nenhuma cidade disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtro de Data */}
          <DateRangeFilterBlur
            value={range}
            loading={loading}
            onChange={(r) => {
              setRange(r);
              setLoading(true);
              // recarregar com novo range
              produtosUsadosOSService
                .buscarOSSEstoque({
                  data_inicio: r.start,
                  data_fim: r.end,
                  assuntos: assuntosSelecionados.length > 0 ? assuntosSelecionados : undefined,
                  cidades: cidadesSelecionadas.length > 0 ? cidadesSelecionadas : undefined,
                })
                .then((response) => {
                  if (response?.success === true && response?.data && typeof response.data === 'object') {
                    setDadosCompilados(response.data);
                    setLoadError(null);
                  } else {
                    throw new Error(response?.error || 'Resposta inválida ao carregar OSs Estoque');
                  }
                  setUltimaAtualizacao(new Date());
                })
                .catch((error) => {
                  console.error('❌ Erro ao carregar dados OSS Estoque:', error);
                  setLoadError(error?.response?.data?.message || error?.message || 'Erro ao carregar OSs Estoque');
                })
                .finally(() => setLoading(false));
            }}
          />
        </div>
      </div>
      <OSSEstoqueDashboard 
        data={dadosFiltrados}
        loading={loading}
      />
    </div>
  );
};

export default OSSEstoque;
